import { Check, Loader2 } from 'lucide-react';
import { type RefObject, useDeferredValue, useMemo, useState } from 'react';

import { multiShiftOutreachMax } from '@apps/web/components/outreaches/edit_shifts_section';
import { Button } from '@apps/web/components/ui/button';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '@apps/web/components/ui/command';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@apps/web/components/ui/popover';
import { useOrganizationTimezone } from '@apps/web/context/timezone_provider';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import { formatDateAndTime } from '@apps/web/lib/date_utils';
import { isCancelledShift } from '@apps/web/lib/shift_cancellation';
import { useDebouncedValue } from '@apps/web/lib/use_debounced_value';
import { cn } from '@apps/web/lib/utils';
import { useShiftSearch } from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { type Shift } from '@libraries/typescript/api_client';

type MultiShiftComboboxProps = {
    readonly values: Array<string>;
    readonly onValuesChange: (values: Array<string>) => void;
    readonly disabled?: boolean;
    readonly shifts?: Array<Shift>;
    readonly isLoading?: boolean;
    readonly scrollLockRef?: RefObject<HTMLElement | null>;
    readonly onLoadMore?: () => void;
    readonly hasMore?: boolean;
    readonly isLoadingMore?: boolean;
    readonly onShiftDiscovered?: (shift: Shift) => void;
    readonly autoFocusSearch?: boolean;
    readonly emptyStateMessage?: string;
};

// Source-system status 'missing' — EHR no longer returns the shift
// (most often because a coordinator edited it and WellSky assigned
// it a new id). Treated like CANCELLED for the combobox.
const isMissingShift = (shift: Shift): boolean => shift.status === 'missing';

// Dead shifts are filtered from the new-selection pool so coordinators
// can't build outreaches against them. Carve-out: a shift already in
// `values` stays visible so the coordinator can see and deselect it —
// an outreach's shift that went missing after creation, or a cancelled
// shift that arrived through an extension deep link.
const isShiftEligibleForPool = (
    shift: Shift,
    selectedIds: ReadonlySet<string>,
): boolean =>
    selectedIds.has(shift.id) ||
    (!isCancelledShift(shift) && !isMissingShift(shift));

export function MultiShiftCombobox({
    values,
    onValuesChange,
    disabled = false,
    shifts,
    isLoading,
    scrollLockRef,
    onLoadMore,
    hasMore,
    isLoadingMore,
    onShiftDiscovered,
    autoFocusSearch = true,
    emptyStateMessage = 'No shifts available.',
}: MultiShiftComboboxProps) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const organizationId = useOrganizationId();
    const { timezone } = useOrganizationTimezone();
    const MIN_REMOTE_SEARCH_LENGTH = 2;
    const rawSearch = search.trim();

    const debouncedSearch = useDebouncedValue(rawSearch, 500);
    const deferredSearch = useDeferredValue(debouncedSearch);
    const activeSearchTerm =
        deferredSearch.length >= MIN_REMOTE_SEARCH_LENGTH ? deferredSearch : '';

    // Memoize params to prevent new Date() from creating a new reference on every render
    const searchParams = useMemo(
        () =>
            activeSearchTerm
                ? { limit: 100, shift_start_date: new Date().toISOString() }
                : undefined,
        [activeSearchTerm],
    );

    const { data: searchResults, isLoading: isSearchingShifts } =
        useShiftSearch(
            organizationId,
            activeSearchTerm || null,
            { reason: 'multi-shift-combobox-search' },
            searchParams,
        );

    // Selected-id set is kept stable so the eligibility filter doesn't
    // churn on every parent render.
    const selectedIdSet = useMemo(() => new Set(values), [values]);

    const remoteShifts = useMemo(() => {
        const shifts = searchResults?.shifts ?? [];
        const now = new Date().toISOString();
        return shifts.filter(
            (shift) =>
                shift.shift_end_time > now &&
                isShiftEligibleForPool(shift, selectedIdSet),
        );
    }, [searchResults, selectedIdSet]);

    const baseShifts = useMemo(
        () =>
            (shifts ?? []).filter((shift) =>
                isShiftEligibleForPool(shift, selectedIdSet),
            ),
        [shifts, selectedIdSet],
    );

    const localFilteredShifts = useMemo(() => {
        if (!rawSearch) {
            return baseShifts;
        }

        const searchLower = rawSearch.toLowerCase();
        return baseShifts.filter((shift) => {
            const patientName = shift.patient_name.toLowerCase();
            const caregiverName = shift.caregiver?.name?.toLowerCase() || '';

            return (
                patientName.includes(searchLower) ||
                caregiverName.includes(searchLower)
            );
        });
    }, [baseShifts, rawSearch]);

    const filteredShifts = useMemo(() => {
        if (activeSearchTerm) {
            if (remoteShifts.length > 0) {
                return remoteShifts;
            }
            if (localFilteredShifts.length > 0) {
                return localFilteredShifts;
            }
            if (isSearchingShifts) {
                return localFilteredShifts;
            }
            return [];
        }

        if (!rawSearch) {
            return baseShifts;
        }

        return localFilteredShifts;
    }, [
        activeSearchTerm,
        baseShifts,
        isSearchingShifts,
        localFilteredShifts,
        remoteShifts,
        rawSearch,
    ]);

    // Selected shifts' client ids, resolved from every pool we know
    // about. A selected id we can't resolve (e.g. preselected outside
    // the fetched window) maps to null, which conservatively keeps the
    // mixed-client cap.
    const selectedClientIds = useMemo(() => {
        const clientIdByShiftId = new Map<string, string | null>();
        for (const shift of [...(shifts ?? []), ...remoteShifts]) {
            clientIdByShiftId.set(shift.id, shift.client_id ?? null);
        }
        return values.map((id) => clientIdByShiftId.get(id) ?? null);
    }, [shifts, remoteShifts, values]);

    const selectionLimit = multiShiftOutreachMax(selectedClientIds);

    // Cap the selection would have if this candidate were added — lets
    // same-client shifts stay selectable up to the higher cap while
    // different-client shifts lock at the mixed cap.
    const limitWithCandidate = (candidateClientId: string | null): number =>
        multiShiftOutreachMax([...selectedClientIds, candidateClientId]);

    const notifyShiftDiscovered = (shiftId: string) => {
        if (!onShiftDiscovered) {
            return;
        }
        const remoteShift = remoteShifts.find((s) => s.id === shiftId);
        if (remoteShift && !baseShifts.some((s) => s.id === shiftId)) {
            onShiftDiscovered(remoteShift);
        }
    };

    const handleSelect = (shiftId: string) => {
        if (values.includes(shiftId)) {
            onValuesChange(values.filter((id) => id !== shiftId));
            return;
        }
        const selectedShift =
            baseShifts.find((s) => s.id === shiftId) ??
            remoteShifts.find((s) => s.id === shiftId);
        if (
            values.length < limitWithCandidate(selectedShift?.client_id ?? null)
        ) {
            onValuesChange([...values, shiftId]);
            notifyShiftDiscovered(shiftId);
        }
    };

    const isLoadingBase =
        !activeSearchTerm && !!isLoading && baseShifts.length === 0;
    const isLoadingRemote = Boolean(
        activeSearchTerm && isSearchingShifts && filteredShifts.length === 0,
    );
    const isDisplayingLoadingState = isLoadingBase || isLoadingRemote;
    const showEmptyState =
        !isDisplayingLoadingState && filteredShifts.length === 0;

    const emptyMessage = (() => {
        if (rawSearch) {
            return 'No results found';
        }
        return emptyStateMessage;
    })();

    return (
        <div className="space-y-2">
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild={true}>
                    <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={open}
                        className="w-full justify-between gap-2 text-left font-normal"
                        disabled={disabled}
                    >
                        {isLoadingBase ? (
                            <span className="flex min-w-0 items-center">
                                <Loader2 className="text-muted-foreground mr-2 h-4 w-4 animate-spin" />
                                <span className="truncate">
                                    Loading shifts…
                                </span>
                            </span>
                        ) : (
                            <span
                                className={cn(
                                    'truncate',
                                    values.length === 0 &&
                                        'text-muted-foreground',
                                )}
                            >
                                {values.length} of {selectionLimit} selected
                            </span>
                        )}
                    </Button>
                </PopoverTrigger>
                <PopoverContent
                    className="z-[60] w-[min(calc(100vw-1rem),440px)] p-0"
                    align="start"
                    onOpenAutoFocus={(event) => {
                        if (!autoFocusSearch) {
                            event.preventDefault();
                        }
                    }}
                    onWheel={(e) => e.stopPropagation()}
                    ref={scrollLockRef as RefObject<HTMLDivElement>}
                >
                    <Command shouldFilter={false}>
                        <CommandInput
                            placeholder="Search by client or caregiver…"
                            value={search}
                            onValueChange={setSearch}
                        />
                        <CommandList
                            className="max-h-[min(68svh,360px)] overscroll-contain"
                            onScroll={(e) => {
                                if (
                                    !onLoadMore ||
                                    !hasMore ||
                                    isLoadingMore ||
                                    activeSearchTerm
                                ) {
                                    return;
                                }
                                const target = e.currentTarget;
                                const nearBottom =
                                    target.scrollHeight - target.scrollTop <=
                                    target.clientHeight + 50;
                                if (nearBottom) {
                                    onLoadMore();
                                }
                            }}
                        >
                            {isDisplayingLoadingState ? (
                                <div className="flex items-center justify-center py-6">
                                    <Loader2 className="text-muted-foreground mr-2 h-4 w-4 animate-spin" />
                                    <span className="text-muted-foreground text-sm">
                                        Searching shifts…
                                    </span>
                                </div>
                            ) : showEmptyState ? (
                                <CommandEmpty>{emptyMessage}</CommandEmpty>
                            ) : (
                                <CommandGroup>
                                    {filteredShifts.map((shift) => {
                                        const isSelected = values.includes(
                                            shift.id,
                                        );
                                        const candidateLimit =
                                            limitWithCandidate(
                                                shift.client_id ?? null,
                                            );
                                        const isDisabled =
                                            !isSelected &&
                                            values.length >= candidateLimit;
                                        return (
                                            <CommandItem
                                                key={shift.id}
                                                value={shift.id}
                                                onSelect={() =>
                                                    handleSelect(shift.id)
                                                }
                                                disabled={isDisabled}
                                                className={cn(
                                                    'flex flex-col items-start py-2',
                                                    isSelected &&
                                                        'bg-accent/60',
                                                    isDisabled &&
                                                        'data-[disabled=true]:pointer-events-auto data-[disabled=true]:cursor-not-allowed data-[disabled=true]:opacity-70',
                                                )}
                                                title={
                                                    isDisabled
                                                        ? `Maximum of ${candidateLimit} shifts can be selected`
                                                        : undefined
                                                }
                                            >
                                                <div className="flex w-full items-center">
                                                    <span
                                                        className={cn(
                                                            'mr-2 flex size-4 shrink-0 items-center justify-center rounded-[4px] border border-zinc-300 dark:border-zinc-600',
                                                            isSelected
                                                                ? 'border-primary bg-primary text-primary-foreground'
                                                                : 'bg-background',
                                                        )}
                                                        aria-hidden={true}
                                                    >
                                                        {isSelected && (
                                                            <Check className="size-3.5" />
                                                        )}
                                                    </span>
                                                    <div className="flex-1">
                                                        <div className="font-medium">
                                                            {shift.patient_name}
                                                        </div>
                                                        <div className="text-muted-foreground text-sm">
                                                            {shift.caregiver
                                                                ?.name ||
                                                                'No caregiver'}{' '}
                                                            •{' '}
                                                            {formatDateAndTime(
                                                                shift.shift_start_time,
                                                                timezone,
                                                            )}
                                                        </div>
                                                    </div>
                                                    {isDisabled && (
                                                        <span className="text-destructive ml-2 text-xs font-medium">
                                                            Selection limit
                                                            reached
                                                        </span>
                                                    )}
                                                </div>
                                            </CommandItem>
                                        );
                                    })}
                                </CommandGroup>
                            )}
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>
            {values.length >= selectionLimit && (
                <p className="text-destructive text-xs font-medium">
                    Selection limit reached. Maximum of {selectionLimit} shifts
                    can be selected.
                </p>
            )}
        </div>
    );
}
